import { isSalesCountryLocale, type SalesCountryLocale } from "@/config/locale";
import { getRuntimeExtraCopy } from "./copy";

export function boundaryLocale(param: unknown, pathname: string | null): SalesCountryLocale | null {
	// A matched route param is authoritative: the proxy rewrites a disabled prefix to its
	// sentinel route, whose param must not be re-read from the visible URL.
	if (typeof param === "string") return isSalesCountryLocale(param) ? param : null;
	const prefix = pathname?.split("/")[1];
	return prefix && isSalesCountryLocale(prefix) ? prefix : null;
}
const catalogKeys = {
	pageTitle: "common.notFound",
	productTitle: "product.notFound",
	home: "common.backToHome",
	products: "common.products",
	partners: "megaMenu.partners",
	back: "localeChannel.back",
	errorTitle: "common.error",
	errorMessage: "auth.genericError",
	retry: "checkout.recovery.retry",
} as const;
export function selectBoundaryLabels(catalog: unknown) {
	return Object.fromEntries(
		Object.entries(catalogKeys).map(([label, path]) => {
			const value = path
				.split(".")
				.reduce<unknown>(
					(parent, key) =>
						parent && typeof parent === "object" ? (parent as Record<string, unknown>)[key] : undefined,
					catalog,
				);
			if (typeof value !== "string" || !value.trim())
				throw new Error(`[i18n] Missing boundary label: ${path}`);
			return [label, value];
		}),
	) as Record<keyof typeof catalogKeys, string>;
}
/** Root error/404 boundaries can render outside next-intl providers. Load only the requested catalog. */
export async function loadBoundaryCopy(locale: SalesCountryLocale) {
	const extra = getRuntimeExtraCopy(locale);
	const catalog = (await import(`@/messages/${locale}.json`)).default as unknown;
	return { ...selectBoundaryLabels(catalog), ...extra };
}
export type BoundaryCopy = Awaited<ReturnType<typeof loadBoundaryCopy>>;
